import * as XLSX from "xlsx";

const n = (v) => (v == null || Number.isNaN(Number(v)) ? "" : Math.round(Number(v) * 100) / 100);

export const ledgerMonthLabel = (key) =>
  new Date(`${key}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });

/**
 * Recommended orders, keyed by the month they ARRIVE (order month + lead
 * time). The ledger stores each order on the month it's placed
 * (`reorder_qty`, `reorder_arrives`, `by_warehouse`); the page shows it where
 * the stock lands. → Map "YYYY-MM" → { qty, order_by, by_warehouse,
 * reorder_date, due_date, overdue }
 *
 * Reorder date: the day to place it so it lands by the start of its arrival
 * month — that day minus the lead time. When that's already past, it's today
 * (`overdue`, with the original `due_date`).
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const isoDay = (d) => d.toISOString().slice(0, 10);

export const poArrivalsOf = (ledger, { today = new Date() } = {}) => {
  const out = new Map();
  const leadDays = Number(ledger?.lead_time_days) || 0;
  const todayKey = isoDay(today);
  (ledger?.months || []).forEach((m) => {
    if (!(m.reorder_qty > 0) || !m.reorder_arrives) return;
    const prev = out.get(m.reorder_arrives) || { qty: 0, order_by: [], by_warehouse: {} };
    prev.qty += m.reorder_qty;
    prev.order_by.push(m.month);
    Object.entries(m.by_warehouse || {}).forEach(([id, q]) => {
      prev.by_warehouse[id] = (prev.by_warehouse[id] || 0) + (Number(q) || 0);
    });
    const due = isoDay(new Date(new Date(`${m.reorder_arrives}-01T00:00:00Z`).getTime() - leadDays * DAY_MS));
    prev.due_date = due;
    prev.overdue = due < todayKey;
    prev.reorder_date = prev.overdue ? todayKey : due;
    out.set(m.reorder_arrives, prev);
  });
  return out;
};

/** "Oct 9, 2026" for a "YYYY-MM-DD". */
export const ledgerDayLabel = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** Hover text for a PO arrival's reorder date. */
export const reorderDateNote = (a) =>
  a.overdue
    ? `It should have been ordered by ${ledgerDayLabel(a.due_date)} to land at the start of the month: order now.`
    : `Order by this date so it lands at the start of the month.`;

/** The ledger as a sheet: one column per month, one row per figure — as on the page. */
export const buildInventoryLedgerSheet = (ledger, { sku } = {}) => {
  const months = ledger?.months || [];
  const arrivals = poArrivalsOf(ledger);
  const nextArrival = arrivals.size ? arrivals.get([...arrivals.keys()].sort()[0]) : null;
  const warehouses = (ledger?.warehouses || []).filter((w) => w.pct > 0);
  const shareSum = warehouses.reduce((s, w) => s + w.pct, 0) || 1;
  const header = [
    "",
    ...months.map((m) => `${ledgerMonthLabel(m.month)}${m.kind === "current" ? " (today)" : ""}`),
  ];
  const line = (label, value) => [label, ...months.map((m) => n(value(m)))];
  const rows = [
    line("On hand (month end)", (m) => m.on_hand),
    line("POs arrived / arriving", (m) => (m.kind === "past" ? m.po_received : m.po_inbound)),
    line("Transfers in / out", (m) => (m.kind === "past" ? m.transfer_received : m.transfer_net)),
    line("Sales", (m) => m.sales),
    line("Days out of stock", (m) => (m.kind === "future" ? null : m.stockout?.days ?? null)),
    line("Stock out, no shipment behind it", (m) => (m.kind === "future" ? null : m.unexplained_moves?.out_units || null)),
    line("Stock in, no receipt behind it", (m) => (m.kind === "future" ? null : m.unexplained_moves?.in_units || null)),
    line("Shortfall", (m) => (m.shortfall > 0 ? m.shortfall : null)),
    line("Forecast", (m) => m.forecast),
    line("PO arrival (recommended)", (m) => (m.kind === "past" ? null : arrivals.get(m.month)?.qty ?? 0)),
    // The two lines under the page's "PO arrival" label: the next reorder date and the lead time.
    ...(nextArrival
      ? [[`  Reorder Date: ${ledgerDayLabel(nextArrival.reorder_date)}`]]
      : []),
    [`  ${n(ledger?.lead_time_days)} days of lead time`],
    ...warehouses.map((w) =>
      line(`  ${w.name || "Warehouse"} (${Math.round((w.pct / shareSum) * 100)}%)`, (m) =>
        arrivals.has(m.month) ? arrivals.get(m.month).by_warehouse[w.id] ?? 0 : null,
      ),
    ),
  ];
  const { bannerItems } = ledgerStockNotes(ledger);
  const waiting = ledger?.waiting_to_ship;
  const info = [
    ["Inventory Ledger", sku || ""],
    ["Stock as of", ledger?.stock_as_of || ""],
    ["Lead time (days)", n(ledger?.lead_time_days)],
    ["Safety stock", n(ledger?.safety_stock)],
    ["Min order qty", n(ledger?.min_order_qty)],
    ...(waiting?.units ? [["Waiting to ship", n(waiting.units), `${waiting.orders} order${waiting.orders === 1 ? "" : "s"}`]] : []),
    ...(bannerItems.length ? [["Note", bannerItems.map((t) => `• ${t}`).join("  ")]] : []),
    [],
  ];
  const sheet = XLSX.utils.aoa_to_sheet([...info, header, ...rows]);
  sheet["!cols"] = [{ wch: 28 }, ...months.map(() => ({ wch: 11 }))];
  return sheet;
};

export const downloadInventoryLedgerXlsx = (ledger, { sku } = {}) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, buildInventoryLedgerSheet(ledger, { sku }), "Inventory Ledger");
  const safe = String(sku || "sku").replace(/[^\w.-]+/g, "_");
  XLSX.writeFile(workbook, `inventory-ledger-${safe}.xlsx`);
};

const periodLabel = (date) =>
  new Date(date).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** One SKU's forecast run as a workbook: Forecast, Reorder and (when there is one) Inventory Ledger sheets. */
export const buildSkuArtifactWorkbook = (run) => {
  const workbook = XLSX.utils.book_new();
  const forecast = (run?.forecast_adjusted || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(
      forecast.map((f) => ({
        Period: periodLabel(f.date),
        Forecast: n(f.predicted_qty),
        Low: n(f.lower),
        High: n(f.upper),
        "Event Uplift": n(f.event_uplift),
      })),
    ),
    "Forecast",
  );
  const m = run?.metrics || {};
  const reorder = [
    ["SKU", run?.sku || ""],
    ["On hand", n(m.total_on_hand)],
    ["Incoming / in transit", n(m.total_in_transit)],
    ["Average daily demand", n(m.avg_daily_demand)],
    ["Lead time (days)", n(m.lead_time_days)],
    ["Safety stock", n(m.safety_stock_used)],
    ["Reorder point", n(m.reorder_point)],
    ["Recommended order quantity", n(m.recommended_order_qty)],
    ["Reorder by", m.reorder_by_date ? periodLabel(m.reorder_by_date) : ""],
    ["Forecast method", run?.forecast_model?.model_label || run?.forecast_model?.model || ""],
    ["Tested error %", n(run?.forecast_model?.tested_error?.wape)],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(reorder), "Reorder");
  if (run?.ledger?.months?.length) {
    XLSX.utils.book_append_sheet(workbook, buildInventoryLedgerSheet(run.ledger, { sku: run.sku }), "Inventory Ledger");
  }
  return workbook;
};

export const downloadSkuArtifactXlsx = (run) => {
  const safe = String(run?.sku || "sku").replace(/[^\w.-]+/g, "_");
  XLSX.writeFile(buildSkuArtifactWorkbook(run), `${safe}_analysis.xlsx`);
};

const dayLabel = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { day: "numeric", month: "short", timeZone: "UTC" });
const isZero = (v) => v != null && Math.round(Number(v)) === 0;

/**
 * Why a month shows no stock and no sales, in plain words, so a 0 isn't read
 * as "no demand" — each fact a separate point, shown as bullets. From the
 * ledger's stock-out days and unexplained stock moves (from the daily
 * snapshots) and the units waiting to ship; runs saved before those existed
 * fall back to "0 on hand and 0 sales".
 *  cellItems(month) → points for that month's on hand / sales cells
 *  moveItems(month) → stock moves with no shipment / receipt behind them
 *  bannerItems      → points about this month, [] when there's nothing to say
 *  cellNote / moveNote / banner → the same, as one line (Excel, plain titles)
 */
export const ledgerStockNotes = (ledger) => {
  const months = ledger?.months || [];
  const trackedFrom = ledger?.stock_tracked_from;
  const cellItems = (m) => {
    if (!m || m.kind === "future") return [];
    const items = [];
    const so = m.stockout;
    if (so?.days > 0) {
      const of = so.tracked_days ? ` of ${so.tracked_days}` : "";
      items.push(`No stock on ${so.days}${of} day${so.days === 1 ? "" : "s"} this month.`);
      items.push("Sales were limited by stock, not demand.");
      if (so.out_at_end && so.since) items.push(`Out of stock since ${dayLabel(so.since)}.`);
      else if (so.back_on) items.push(`Back in stock ${dayLabel(so.back_on)}.`);
    } else if (isZero(m.on_hand) && isZero(m.sales)) {
      items.push("No stock and no sales.");
      items.push("Likely out of stock, so there was nothing to sell.");
    }
    if (items.length && trackedFrom && trackedFrom.slice(0, 7) === m.month && trackedFrom.slice(8) !== "01") {
      items.push(`Daily stock is tracked from ${dayLabel(trackedFrom)}, so earlier days aren't counted.`);
    }
    return items;
  };

  // Stock that moved with no shipment / receipt behind it (from the daily snapshot).
  const moveItems = (m) =>
    (m?.kind !== "future" ? m?.unexplained_moves?.events || [] : []).map((e) => {
      const n = Math.round(e.unexplained);
      const unitsTxt = `${n} unit${n === 1 ? "" : "s"}`;
      return e.change < 0
        ? `Stock dropped by ${unitsTxt} on ${dayLabel(e.day)} (${Math.round(e.from)} → ${Math.round(e.to)}) with no shipped order behind it: likely a stock adjustment.`
        : `Stock rose by ${unitsTxt} on ${dayLabel(e.day)} (${Math.round(e.from)} → ${Math.round(e.to)}) with no PO or transfer receipt behind it: likely a delivery not recorded as a PO receipt, a return, or a stock adjustment.`;
    });

  const current = months.find((m) => m.kind === "current");
  const waiting = ledger?.waiting_to_ship;
  const bannerItems = [];
  if (current) {
    const idx = months.indexOf(current);
    const prev = idx > 0 ? months[idx - 1] : null;
    const so = current.stockout;
    const outNow = so ? so.out_at_end : isZero(current.on_hand);
    const noSales = isZero(current.sales);
    const since = so?.since ? ` since ${dayLabel(so.since)}` : "";
    if (outNow && waiting?.units > 0) {
      const n = Math.round(waiting.units);
      bannerItems.push(`Out of stock${since}.`);
      bannerItems.push(`${n} unit${n === 1 ? " has" : "s have"} been ordered and ${n === 1 ? "is" : "are"} waiting to ship (backordered).`);
      bannerItems.push("Sales here are orders placed, not shipped.");
    } else if (outNow && (so?.days > 0 || noSales)) {
      bannerItems.push(`Out of stock${since}: there is nothing left to sell.`);
      bannerItems.push(noSales ? "This month's sales are 0." : "This month's sales stopped there.");
      if (current.forecast > 0) bannerItems.push(`The forecast (${Math.round(current.forecast)}) is demand the stock can't cover, not expected sales.`);
      if (noSales && prev?.on_hand > 0) {
        const u = Math.round(prev.on_hand);
        bannerItems.push(
          `The ${u} unit${u === 1 ? "" : "s"} on hand at the end of ${ledgerMonthLabel(prev.month)} had most likely already been sold: on hand drops when an order ships, not when it's placed, and those sales count in ${ledgerMonthLabel(prev.month)}.`,
        );
      }
    }
  }
  const line = (items) => (items.length ? items.join(" ") : null);
  return {
    cellItems,
    moveItems,
    bannerItems,
    cellNote: (m) => line(cellItems(m)),
    moveNote: (m) => line(moveItems(m)),
    banner: line(bannerItems),
  };
};
